import { createDOMCanvas, Paintable, Size } from 'ff-canvas';
import { Json } from '@gift/types/Json';
import { BasicSceneOptions } from '@gift/basic-scene-core/BasicSceneOptions';
import { createPipeline } from '@gift/common/helpers/createPipeline';
import { noop } from 'ff-canvas/dist/util/noop';
import { createBackgroundPaintable } from './preview/helpers/createBackgroundPaintable';
import { BasicVariantOptions } from '@gift/basic-scene-core/BasicVariantOptions';
import { createPaintables } from '@gift/common/helpers/createPaintables';
import { toBlob } from '@gift/common/helpers/toBlob';
import { maintainAspectRatioOf } from '@gift/common/helpers/maintainAspectRatioOf';
import { createTransform, createUndoViewbox, TransformParameters, UndoViewbox } from '@gift/common/helpers/transformParameters';
import { BasicSceneMeta } from '@gift/basic-scene-core/BasicSceneMeta';

export const renderThumbnail = async (
    project: Json.Project<BasicSceneOptions, BasicVariantOptions, BasicSceneMeta>,
    transformParameters: TransformParameters,
    outputSize: Size
): Promise<Blob> => {

    const { scene, variant, variables, inputs } = project;

    const paintables = createPaintables(variables, inputs, variant, 'mockup');
    const sceneSize = {
        width: scene.options.width,
        height: scene.options.height
    };

    const maintainedSceneSize = maintainAspectRatioOf(sceneSize, outputSize);
    const maintainedTransformParameters: TransformParameters = {
        ...transformParameters,
        sceneWidth: maintainedSceneSize.width,
        sceneHeight: maintainedSceneSize.height,
    };

    if (project.variant.options.backgroundAssetUrl) {
        paintables.push(createMockupPaintable(scene, variant, createUndoViewbox(maintainedTransformParameters)));
    }

    const thumbnail = await render(paintables, outputSize, maintainedTransformParameters);
    return await toBlob(thumbnail);
};

const createMockupPaintable = (
    scene: Json.Scene<BasicSceneOptions>,
    variant: Json.Variant<BasicVariantOptions>,
    undoViewbox: UndoViewbox
) => {
    return createBackgroundPaintable(
        scene.options.width, scene.options.height,
        undoViewbox,
        variant.options.backgroundAssetUrl!);
};

const render = async (
    paintables: Paintable[],
    outputSize: Size,
    transformParameters: TransformParameters
) => {

    const canvas = createDOMCanvas();
    canvas.width = outputSize.width;
    canvas.height = outputSize.height;

    const transform = createTransform(outputSize.width, outputSize.height, transformParameters);
    const pipeline = createPipeline(canvas, {
        requestRender: noop,
        transform: transform,
        useDevicePixelRatio: false
    });

    await pipeline.preloadResources(paintables);
    pipeline.paintAll(paintables);
    return canvas;
};